import { ReactNode, useMemo } from 'react';

import { OrganizationApiProvider } from '@gasbi/shared/web/Organization';
import { useDispatch } from 'react-redux';

import { useGetDepartmentsIInfiniteQuery } from '@/features/departments/departmentsApi';
import { useGetEmployeePositionsIInfiniteQuery } from '@/features/employeePositions/employeePositionsApi';
import {
  employeesApi,
  useGetEmployeesAccessibleByOrganizationQuery,
  useGetEmployeesByRoleInfiniteQuery,
  useGetEmployeesInfiniteQuery,
  useGetLegacyEmployeeByIdQuery,
} from '@/features/employees/employeesApi';
import {
  useGetSettlementsQuery,
  useGetStreetsQuery,
} from '@/features/locations/locationsApi';
import {
  organizationsApi,
  useGetCachedOrganizationByIdSelector,
  useGetOrganizationByIdQuery,
  useGetOrganizationRolesInfiniteQuery,
  useGetOrganizationsInfiniteQuery,
  useUpdateOrganizationByIdMutation,
} from '@/features/organizations/organizationsApi';
import { permissionsApi } from '@/features/permissions';
import {
  useAssignRoleMutation,
  useGetCachedRoleByIdSelector,
  useGetRoleByIdQuery,
  useGetSystemRolesQuery,
  useSaveRoleMutation,
  useSuspendRoleMutation,
} from '@/features/roles/hooks';
import { organizationRolesApi } from '@/features/roles/organizationRolesApi';
import {
  useGetRoleAvailablePermissionsByIdQuery,
  useGetRolePermissionsByIdQuery,
} from '@/features/roles/rolesApi';
import { RootRoleId } from '@/features/roles/types';
import { getRoleLabel, getRoleType, isRootRole } from '@/features/roles/utils';
import {
  subdivisionsApi,
  useGetSubdivisionsInfiniteQuery,
} from '@/features/subdivisions/subdivisionsApi';

interface Props {
  children: ReactNode;
}

/**
 * The legacy organization pages of @gasbi/shared/web/Organization the app keeps (ГРМ card, structural subdivisions,
 * roles), over BMS's billing-organizations and structural-subdivisions and Households Billing's shared RBAC employees
 * and roles (GSB-8308).
 */
const OrganizationApiContextWrapper = ({ children }: Props) => {
  const dispatch = useDispatch();

  const value = useMemo(
    () => ({
      organizationsApi: {
        ...organizationsApi,
        useGetOrganizationRolesInfiniteQuery,
        useGetCachedOrganizationByIdSelector,
        useGetOrganizationsInfiniteQuery,
      },
      // BMS has no head office view (PO, 2026-10-26: it is Distribution Admin's «Адміністрація»). The legacy pages
      // still call these hooks whatever the view, so they are the organization's own and never reach a head office
      // endpoint.
      mainOrganizationApi: {
        useGetMainOrganizationByIdQuery: useGetOrganizationByIdQuery,
        useGetMainOrganizationRolesInfiniteQuery:
          useGetOrganizationRolesInfiniteQuery,
        useUpdateMainOrganizationByIdMutation:
          useUpdateOrganizationByIdMutation,
      },
      rolesApi: {
        useGetSystemRolesQuery,
        useGetRoleByIdQuery,
        useGetCachedRoleByIdSelector,
        useAssignRoleMutation,
        useSuspendRoleMutation,
        useSaveRoleMutation,
        useGetRolePermissionsByIdQuery,
        useGetRoleAvailablePermissionsByIdQuery,
      },
      employeesApi: {
        ...employeesApi,
        useGetEmployeeByIdQuery: useGetLegacyEmployeeByIdQuery,
        useGetEmployeesAccessibleByOrganizationQuery,
        useGetEmployeesByRoleInfiniteQuery,
        useGetEmployeesInfiniteQuery,
        useGetDepartmentsIInfiniteQuery,
        useGetEmployeePositionsIInfiniteQuery,
        canReactivateEmployee: true,
      },
      subdivisionsApi: {
        ...subdivisionsApi,
        useGetSubdivisionsInfiniteQuery,
      },
      locationsApi: {
        useGetSettlementsQuery,
        useGetStreetsQuery,
      },
      utils: {
        invalidateOrgRolesTags: () =>
          dispatch(organizationRolesApi.util.invalidateTags(['Roles'])),
        invalidateMainOrgRolesTags: () => {},
        invalidateEmployeesByRoleTags: () =>
          dispatch(employeesApi.util.invalidateTags(['EmployeesByRole'])),
        invalidateUserPermissionsTags: () =>
          dispatch(permissionsApi.util.invalidateTags(['UserPermissions'])),
      },
      common: {
        RootRoleId,
        isRootRole,
        getRoleLabel,
        getRoleType,
      },
      readOnlyOrganizationStructure: true,
      // GSB-8452: a підрозділ's «Співробітники» are the people with roles on its node.
      nodeEmployees: true,
      // The представник (ГРМ and підрозділ) and the підписант (підрозділ only) are BMS's, assigned in Distribution and
      // Distribution Admin (PO, 2026-10-30; PO, 2026-10-31, GSB-8532).
      withoutRepresentativeAndSignatory: true,
    }),
    [dispatch],
  );

  return (
    <OrganizationApiProvider value={value}>{children}</OrganizationApiProvider>
  );
};

export default OrganizationApiContextWrapper;
